/**
 * En qué dirección se dice una tasa para que se entienda.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL PROBLEMA, CON LAS PALABRAS DEL DUEÑO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * «Si tengo de moneda principal COP, todo el sistema de tasas se daña; solo
 * funciona con monedas de alto valor».
 *
 * Tiene razón, y la causa es una sola decisión que se tomó cuando la única
 * moneda principal que había en la cabeza era el dólar: **la tasa se guarda y
 * se pregunta siempre como «1 principal = X de la otra»**.
 *
 * Con el dólar de principal eso se lee solo: «1 USD = 36,50 VES». Con el peso
 * colombiano de principal, la misma frase dice:
 *
 *     1 COP = 0,000244 USD
 *
 * Y eso está roto por tres sitios a la vez:
 *
 *   1. **No se puede escribir.** Nadie teclea 0,000244 sin equivocarse, y un
 *      cero de más o de menos multiplica o divide por diez todos los precios
 *      del negocio.
 *   2. **No se puede leer.** Un bodeguero colombiano sabe al instante si 4.100
 *      está bien o mal; 0,000244 no le dice nada, así que un error se queda
 *      puesto.
 *   3. **Se redondeaba mal.** La casilla recortaba a seis decimales, y con seis
 *      decimales 0,000244 ya no es la tasa: es otra parecida. Guardar sin tocar
 *      nada empeoraba la tasa un poquito cada vez.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LA SOLUCIÓN: LA CUENTA NO CAMBIA, LA FRASE SÍ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Por dentro se sigue guardando igual —«X de la otra por cada 1 principal»— y
 * el servidor no se entera de nada. Lo que cambia es que la pantalla dice y
 * pregunta la tasa **en la dirección en la que se habla**:
 *
 *     1 USD = 4.100 COP
 *
 * Que es exactamente lo mismo, del derecho.
 */

/**
 * Cuántos decimales guarda el servidor (`RATE_DECIMALS` en `core/money.py`).
 *
 * ⚠️ Importa aquí porque al dar la vuelta a una tasa se divide, y una división
 * hay que cortarla en algún sitio. Cortar donde corta el servidor evita guardar
 * un número más largo del que cabe, que el servidor redondearía de todas formas
 * y dejaría la casilla enseñando algo distinto de lo guardado.
 */
const DECIMALES_GUARDADOS = 10;

/**
 * ¿Esta tasa se entiende mejor al revés?
 *
 * La regla es la que usa cualquiera al hablar: **se pone delante la moneda que
 * vale más**. «1 USD = 4.100 COP», nunca «1 COP = 0,000244 USD».
 *
 * Con la tasa ya puesta se sabe mirando el número: por debajo de 1, la otra
 * moneda vale más que la principal.
 */
export function conviene_invertir(tasa: string | number | null | undefined): boolean {
  const n = typeof tasa === "number" ? tasa : Number.parseFloat(String(tasa ?? ""));
  return Number.isFinite(n) && n > 0 && n < 1;
}

/**
 * Y cuando todavía NO hay tasa —se está agregando la moneda— hay que adivinar.
 *
 * ⚠️ Se adivina por los decimales de la moneda principal, y no es un truco:
 * una moneda sin decimales es una moneda en la que los precios se escriben con
 * cuatro o cinco cifras. El peso colombiano, el chileno y el guaraní no tienen
 * decimales justamente porque un céntimo no compra nada.
 *
 * Así que si la principal no tiene decimales, casi seguro es la «grande» y la
 * frase natural es «1 dólar = tantos pesos». Si se falla, se cambia con un
 * toque: al lado de la casilla está el selector.
 */
export function invertidaPorDefecto(decimalesDelPrincipal: number): boolean {
  return decimalesDelPrincipal === 0;
}

/** Le da la vuelta a una tasa: 4100 ↔ 0,0002439024. */
export function invertir(valor: string | number | null | undefined): string {
  const n = typeof valor === "number" ? valor : Number.parseFloat(String(valor ?? ""));
  if (!Number.isFinite(n) || n <= 0) return "";
  return recortar(1 / n);
}

/**
 * Igual, pero para ENSEÑARLO, no para guardarlo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ESTO ARREGLA UN «4100,0007» QUE SALÍA DE LA NADA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El servidor guarda las tasas con diez decimales. Al escribir «4.100» se
 * guarda 0,0002439024, que es 1/4100 cortado ahí — y al darle la vuelta otra
 * vez para volver a enseñarlo sale 4100,0007, no 4100.
 *
 * En dinero eso no es nada: la diferencia en un producto de 410.000 pesos es de
 * seis milésimas de dólar. Pero en pantalla es horrible — escribiste 4.100 y la
 * casilla te contesta 4.100,0007, que se lee como «esto entendió otra cosa».
 *
 * Seis cifras significativas se llevan por delante la cola de la división y
 * dejan sitio de sobra para cualquier tasa que exista.
 *
 * ⚠️ El límite, dicho claro: una moneda principal cuyas tasas se cuenten por
 * MILLONES (un bolívar de los de antes de la reconversión) empieza a perder
 * precisión de verdad, porque diez decimales solo dan siete cifras buenas al
 * invertir. El día que eso haga falta, lo que hay que ampliar es la columna del
 * servidor (`RATE_DECIMALS` en `core/money.py`), no esto.
 */
function invertirParaLeer(valor: string | number | null | undefined): string {
  const vuelta = invertir(valor);
  if (!vuelta) return "";
  const n = Number.parseFloat(Number.parseFloat(vuelta).toPrecision(6));
  return String(n);
}

/**
 * Corta una tasa donde la corta el servidor, sin ceros de relleno.
 *
 * ⚠️ Con cifras significativas y no con decimales fijos. Una tasa puede ser
 * 4.100 o 0,000244 y las dos tienen que sobrevivir: `toFixed(6)` deja la
 * primera intacta y destroza la segunda.
 */
function recortar(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "";

  /*
    Dar la vuelta dos veces no devuelve exactamente el mismo número —1/(1/4100)
    sale 4100,0000164— y esa cola de basura acabaría dentro de la casilla, que
    es de las cosas que más desconfianza dan: escribiste 4100 y te enseña
    4100,000016.

    Ocho cifras significativas se llevan la cola por delante y siguen dejando
    sitio de sobra para cualquier tasa de verdad.
  */
  const conCifras = Number.parseFloat(n.toPrecision(8));
  const texto = conCifras.toFixed(DECIMALES_GUARDADOS);
  return texto.replace(/0+$/, "").replace(/\.$/, "");
}

/**
 * La tasa como se escribe en la casilla, en la dirección que se esté usando.
 *
 * Entra siempre lo GUARDADO (unidades de la otra moneda por cada 1 principal).
 */
export function tasaParaEscribir(guardado: string | null | undefined, invertida: boolean): string {
  if (!guardado) return "";
  if (!invertida) {
    const n = Number.parseFloat(guardado);
    return Number.isFinite(n) && n > 0 ? recortar(n) : "";
  }
  return invertirParaLeer(guardado);
}

/** Y de vuelta: lo que se escribió, convertido a lo que se guarda. */
export function tasaParaGuardar(escrito: string, invertida: boolean): string {
  if (!escrito.trim()) return "";
  if (!invertida) return escrito.trim();
  return invertir(escrito);
}

/**
 * La tasa de una moneda contra otra, pasando por el dólar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL DATO QUE LA GENTE TIENE NO ES EL QUE LE PEDÍAMOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El dueño, con el peso colombiano de moneda principal y bolívares que cobrar:
 *
 *   «no sé cómo sería lo de Bs a COP... no sé cómo sería de 1 COP a 1 Bs o
 *    viceversa jajaja»
 *
 * Y no es que no sepa: es que **ese número no existe en la calle**. Nadie
 * publica el bolívar en pesos. Lo que todo el mundo sabe, en Caracas y en
 * Cúcuta, es a cómo está EL DÓLAR en su moneda:
 *
 *   · 1 USD = 4.100 COP
 *   · 1 USD = 36,50 VES
 *
 * De ahí sale el cruce solo: 1 Bs son 4.100 / 36,50 = 112,33 pesos. Es una
 * división que Softfi puede hacer y que el dueño estaba haciendo —o no
 * haciendo— con la calculadora del teléfono.
 *
 * La cuenta, en las unidades en que se guardan las tasas:
 *
 *   tasa(VES) = tasa(USD) × «cuántos VES por 1 USD»
 *             = 0,000243902 × 36,5
 *             = 0,008902  →  y al leerlo: 1 VES = 112,33 COP
 *
 * `tasaDelPuente` es lo GUARDADO del dólar (dólares por 1 unidad de la moneda
 * principal) y `porCadaPuente` es lo que la persona sabe de memoria.
 */
export function tasaCruzada(
  tasaDelPuente: string | number | null | undefined,
  porCadaPuente: string | number | null | undefined,
): string {
  const puente =
    typeof tasaDelPuente === "number" ? tasaDelPuente : Number.parseFloat(String(tasaDelPuente ?? ""));
  const cuantas =
    typeof porCadaPuente === "number" ? porCadaPuente : Number.parseFloat(String(porCadaPuente ?? ""));

  if (!Number.isFinite(puente) || puente <= 0) return "";
  if (!Number.isFinite(cuantas) || cuantas <= 0) return "";

  return recortar(puente * cuantas);
}

/**
 * Los dos lados de la frase, ya puestos en orden.
 *
 * `leerTasa("0.000244", "COP", "USD")` → «1 USD = 4.098,36 COP»
 * `leerTasa("36.50", "USD", "VES")`    → «1 USD = 36,50 VES»
 */
/**
 * La tasa guardada, dicha contra OTRA moneda que no es la principal.
 *
 * Lo guardado es «cuántos COP por cada 1 USD». Si el bolívar está a 36,50 por
 * dólar y el peso a 4.015, entonces 1 VES son 4.015 / 36,50 = 110 COP. Es la
 * división de siempre, y es lo que deja escribir el COP desde el VES.
 */
export function tasaRespectoDe(
  guardado: string | number | null | undefined,
  tasaDeReferencia: string | number | null | undefined,
): string {
  const tasa = typeof guardado === "number" ? guardado : Number.parseFloat(String(guardado ?? ""));
  const referencia =
    typeof tasaDeReferencia === "number"
      ? tasaDeReferencia
      : Number.parseFloat(String(tasaDeReferencia ?? ""));
  if (!Number.isFinite(tasa) || tasa <= 0) return "";
  if (!Number.isFinite(referencia) || referencia <= 0) return "";
  return recortar(tasa / referencia);
}

/** Dónde se recuerda, en este aparato, contra qué moneda se escribe cada tasa. */
function claveDeReferencia(principal: string, moneda: string): string {
  return `softfi:tasa-contra:${principal}:${moneda}`;
}

/** Contra qué moneda se escribió esta tasa la última vez, en este aparato. */
export function leerReferencia(principal: string, moneda: string): string | null {
  try {
    return localStorage.getItem(claveDeReferencia(principal, moneda));
  } catch {
    return null;
  }
}

export function recordarReferencia(principal: string, moneda: string, codigo: string): void {
  try {
    if (codigo === principal) localStorage.removeItem(claveDeReferencia(principal, moneda));
    else localStorage.setItem(claveDeReferencia(principal, moneda), codigo);
  } catch {
    // Sin almacenamiento (navegación privada) se usa la principal, y ya.
  }
}

/**
 * La tasa dicha contra la moneda que se eligió al escribirla: si el COP se
 * sacó del bolívar, «1 VES = 3,2 COP» y no «1 USD = 2.712,64 COP».
 */
export function leerTasaContra(
  guardado: string | null | undefined,
  moneda: string,
  referencia: { codigo: string; tasa: string },
): { izquierda: string; derecha: string; cuanto: string; invertida: boolean } | null {
  const relativa = tasaRespectoDe(guardado, referencia.tasa);
  if (!relativa) return null;
  const n = Number.parseFloat(relativa);
  if (conviene_invertir(n)) {
    return { izquierda: moneda, derecha: referencia.codigo, cuanto: invertirParaLeer(n), invertida: true };
  }
  return { izquierda: referencia.codigo, derecha: moneda, cuanto: recortar(n), invertida: false };
}

export function leerTasa(
  guardado: string | null | undefined,
  principal: string,
  moneda: string,
): { izquierda: string; derecha: string; cuanto: string; invertida: boolean } | null {
  if (!guardado) return null;
  const n = Number.parseFloat(guardado);
  if (!Number.isFinite(n) || n <= 0) return null;

  if (conviene_invertir(n)) {
    return { izquierda: moneda, derecha: principal, cuanto: invertirParaLeer(n), invertida: true };
  }
  return { izquierda: principal, derecha: moneda, cuanto: recortar(n), invertida: false };
}
